import { cellOrOverflow } from '@/lib/coldroomPlacement'
import { useState, useEffect, useRef, useMemo } from 'react'
import { POLL } from '@/lib/pollTiers'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useReturnHereState } from '@/lib/returnTo'
import { uuidParam } from '@/modules/shared/compliance/complianceAddress'
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom'
import { tippingBatchParam } from './PrecoolStageAction'
import { supabase } from '@/lib/supabase'
import { ValidationError } from '@/lib/errorReporter'
import { validateTip, canSubmitTip, tippedPercent, clampBinQty } from '@/lib/tippingMath'
import { isOptlk } from '@/lib/optimistic'
import { safeDecimal } from '@/lib/numericInput'
import { insertOrQueueTipping, tipOrQueueBatch } from '@/lib/offlineQueue'
import type { Delivery, Run } from '@/types/database'
import { ChevronRight, ExternalLink, Layers, Package, Scale, ScanLine, Undo2 } from 'lucide-react'
import TeachingEmptyState from '@/components/TeachingEmptyState'
import { AddDeliveryToRunButton } from './AddDeliveryToRunButton'
import QueryStateGate from '@/components/QueryStateGate'
import { RunStatusBadge } from '@/components/RunSelector'
import PageHeader from '@/components/PageHeader'
import { Button, StatusPill } from '@/components/ui'
import { useCanStartRun } from '@/hooks/useCanStartRun'
import { useCanOpenModule } from '@/hooks/useCanOpenModule'
import { newRunHref, dollyMatchesRun, splitDolliesForRun } from '@/lib/runsRegister'
import MutationOverlays from '@/components/MutationOverlays'
import { useRunLifecycle } from './useRunLifecycle'
import { runActions, type RunStatus } from '@/lib/runStatus'
import PickRunFirst, { type RunWaitingRead } from '@/components/PickRunFirst'
import { fetchUserNames } from '@/hooks/useAssigneeNames'
import { sastHM } from '@/lib/dates'
import { usePersistedState } from '@/hooks/usePersistedState'
import { useTenantPersistedState } from '@/hooks/useTenantPersistedState'
import { useScanWedge } from '@/hooks/useScanWedge'
import { useActiveTenantId } from '@/hooks/useActiveTenantId'
import { matchBatchNumber, matchBinNumber, matchIntakeSticker, deliveryIdFromLabel, deliveryLabelCode, tippingLabelFromTexts, isDeliveryLabel, isDeliveryPalletLabel } from '@/lib/batchScan'
import { palletOfText, palletScanRefusal, palletTipGrossKg, withPalletTipped } from '@/lib/deliveryPallets'
import { fetchPallet, fetchPalletsFor, type PalletWithTip } from '@/lib/deliveryPalletsDb'
import { useActiveTenant } from '@/lib/useActiveTenant'
import { capabilityOn } from '@/lib/capabilityCatalog'
import { decideDeliveryScan, intakeTipGrossKg } from '@/lib/intakeWeight'
import { linkDeliveryToRun, invalidateAfterLink } from '@/lib/linkDeliveryToRun'
import { createFrameDecoder } from '@/lib/badgeCameraDecode'
import BadgeCameraScanModal from '@/components/BadgeCameraScanModal'
import { playScanBeep, playTone } from '@/lib/tone'
import { fmtQty } from '@/lib/numberFormat'
import { fmtKg } from '@/lib/runsKpis'
import SubmitOverlay from '@/components/SubmitOverlay'
import { TIPPING_DOMAIN, tippingRefusal } from '@/lib/packhouseErrorDomains'
import { useCurrentUser } from '@/lib/useCurrentUser'
import { usePermissions } from '@/lib/usePermissions'
import { tipPermissionRefusal, TIP_NEEDS_PERMISSION } from '@/lib/packhouseErrorDomains'
import { isManagerOrAbove, isAdmin } from '@/lib/roles'
import { useProductionFlow } from '@/lib/useProductionFlow'
import { useFlowManifest } from '@/hooks/useCommodityTemplates'

import { confirmDialog, alertDialog, promptDialog } from '@/components/Dialogs'
import { canUndoTip } from '@/lib/canUndoTip'
import { confirmUndoTip } from '@/components/confirmUndoTip'
import { withTimeout } from '@/lib/saveTimeout'
import { useSaveDb, ensureSaveDb } from '@/lib/useSaveDb'
import DateInput from '@/components/DateInput'
import { sastTodayYMD } from '@/lib/dates'
import { newUuid } from '@/lib/uuid'
import TipNetPreview, { useTipNetKg } from './TipNetPreview'
import TipRangeHint from './TipRangeHint'
import RecentTips from './RecentTips'
import { binRefusal, feedWarning, isMixedRun, canSplit, cratesLeftAfterTip, parsePortionCrates, tipBinQuestion, type LinkedRunFields } from '@/lib/linkedRuns'
import BinReprintNotice from './BinReprintNotice'
// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
/** A scanned pallet sticker (spec #4006): the pallet, and how many its delivery has. */
type ScannedPallet = PalletWithTip & { count: number }

interface TippingEntry {
  tipping_id: string
  bin_quantity: number
  gross_weight_kg: number
  net_weight_kg: number
  tipped_at: string
}

// A RELEASED cooling batch ready to tip (berry flow), with its source
// delivery's grower/PUC so the operator can tell same-variety dollies apart.
interface ReadyBatch {
  batch_id:      string
  batch_number:  string
  crate_type:    string
  crate_count:   number
  net_weight_kg: number
  variety:       string | null
  released_at:   string | null
  grower:        string | null
  puc:           string | null
}

// BF-04 (mig 20261032960000): writing a tip needs the Tipping "Edit"
// permission — the tipping_insert policy and both tip RPCs check it, the same
// right undo_tipping has always needed. A role without it still sees the board
// (what is ready, what was tipped) but gets no tip control, and this says why.
const TIP_VIEW_ONLY =
  'View only: tipping needs the Tipping "Edit" permission on your role. Ask an admin to add it.'

// "2h ago" / "35m ago" — how long a released dolly has been waiting to tip.
function agoLabel(iso: string | null): string | null {
  if (!iso) return null
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000))
  if (mins < 60) return `${mins}m ago`
  const h = Math.floor(mins / 60)
  return h < 48 ? `${h}h ago` : `${Math.floor(h / 24)}d ago`
}

// ---------------------------------------------------------------------------
// Tipping form for a single delivery
// ---------------------------------------------------------------------------
// Pre-existing large form (per-delivery tip capture + history).
// fallow-ignore-next-line complexity
function TippingForm({
  delivery,
  activeRun,
  onDone,
  waiting,
  onOpenNext,
  pallet,
}: {
  delivery: Delivery
  activeRun: Run
  onDone: () => void
  /** A scanned pallet sticker (spec #4006): its crates are tipped whole, with
   *  its intake weight on offer, and the tip records the pallet. */
  pallet?: ScannedPallet | null
  /** UF-07: the run's other deliveries still to tip, as the board reads them. */
  waiting?: { status: 'loading' | 'error' | 'ready'; items: Delivery[] }
  onOpenNext?: (d: Delivery) => void
}) {
  const returnHere = useReturnHereState()
  const qc = useQueryClient()
  const saveDb = useSaveDb()
  // UF-06: the form stays ready for the next weigh. Bin Quantity keeps what this
  // device last tipped (a line that tips threes stays on 3); the first tip on a
  // device opens at 2. The cursor does NOT go back into Gross Weight after a tip:
  // a focus() after an async save can raise the on-screen keyboard over the board on
  // a floor handset, and that is still to be tested on a real Blackview.
  const [lastBinQty, setLastBinQty] = usePersistedState<number>('kf49.tipping.lastBinQty', 2)
  const [binQty,  setBinQty]  = useState(() =>
    pallet ? pallet.crates : Number.isInteger(lastBinQty) && lastBinQty >= 1 ? lastBinQty : 2)
  const [savedNote, setSavedNote] = useState<string | null>(null)
  const [grossKg, setGrossKg] = useState('')
  const [error,   setError]   = useState<string | null>(null)

  const gross   = parseFloat(grossKg) || 0

  // Live bins_remaining for this delivery — the prop is a snapshot from
  // when the form opened, so it goes stale after each tip and the cap +
  // validation guard read the wrong number (you could tip past 0). Refetched
  // after every tip via invalidate below.
  const { data: liveDelivery } = useQuery({
    queryKey: ['delivery-live', delivery.delivery_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('deliveries')
        .select('bins_received, bins_remaining')
        .eq('delivery_id', delivery.delivery_id)
        .single()
      if (error) throw error
      return data as { bins_received: number; bins_remaining: number }
    },
    initialData: { bins_received: delivery.bins_received, bins_remaining: delivery.bins_remaining },
    // U12 D4: a second station tipping from this delivery shows within 30 s
    // (react-query pauses the interval while the tab is hidden).
    refetchInterval: POLL.live,
  })
  const binsReceived  = liveDelivery?.bins_received  ?? delivery.bins_received
  const binsRemaining = liveDelivery?.bins_remaining ?? delivery.bins_remaining
  const netKg = useTipNetKg(delivery.delivery_id, gross, binQty)
  // Spec #3956 §4: a delivery weighed at intake, tipped whole in one go, can
  // take its intake weight (gross less the pallet) instead of a second weigh.
  const intakeGross = pallet ? palletTipGrossKg(pallet) : intakeTipGrossKg(delivery, binQty, binsRemaining)

  // Clamp bin qty if the live remaining drops below the current selection
  // (e.g. another operator tipped from a different device).
  // U12 D4: when a REFETCH (not this device's own tip) does it, say so once.
  const [anotherStationNote, setAnotherStationNote] = useState<number | null>(null)
  const prevRemaining = useRef(binsRemaining)
  const ownTippedBins = useRef(0)
  useEffect(() => {
    const drop = prevRemaining.current - binsRemaining
    prevRemaining.current = binsRemaining
    if (binsRemaining <= 0) setAnotherStationNote(null)
    if (drop > 0) {
      const own = Math.min(drop, ownTippedBins.current)
      ownTippedBins.current -= own
      if (drop > own && binsRemaining > 0 && binQty > binsRemaining) setAnotherStationNote(binsRemaining)
    }
    if (binsRemaining > 0 && binQty > binsRemaining) setBinQty(clampBinQty(binQty, binsRemaining))
  }, [binsRemaining, binQty])

  // Tipping history for this delivery on this run
  const { data: history = [] } = useQuery({
    queryKey: ['tipping-history', delivery.delivery_id, activeRun.run_id],
    refetchInterval: POLL.live,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tipping_log')
        .select('tipping_id, bin_quantity, gross_weight_kg, net_weight_kg, tipped_at')
        .eq('delivery_id', delivery.delivery_id)
        .eq('run_id', activeRun.run_id)
        .order('tipped_at', { ascending: false })
      if (error) throw error
      return data as TippingEntry[]
    }
  })

  const totalBinsTipped  = history.reduce((s, t) => s + t.bin_quantity, 0)
  const totalNetWeightKg = history.reduce((s, t) => s + t.net_weight_kg, 0)

  const { data: me } = useCurrentUser()
  const { can: canUndoPerm, isCustomRole: undoCustomRole } = usePermissions()
  // undo_tipping RPC = tipping:edit (mig …p7); custom roles holding it can undo.
  const canUndo = canUndoTip(me?.role, canUndoPerm, undoCustomRole)
  // Custom roles holding pallets:override share the back-date trust class
  // (mig 20260707070653 — same key as the pallet/delivery back-dates).
  const { can: canPerm, data: permsMap } = usePermissions()
  // BF-04: the Tip button only for a role holding tipping:edit (what the
  // server now checks). The "why" shows only when a READ map says no — a map
  // still loading (undefined) or unreadable offline (null) never claims the
  // operator lacks the right.
  const canTip = canPerm('tipping', 'edit')
  const tipBlocked = !!permsMap && !canTip
  // Back-date affordance for paper-record capture. Admins always; non-admin
  // operators with the can_override_delivery_date trust flag too (same flag the
  // Deliveries + Pallets back-dates use — one toggle, all back-date affordances).
  const canBackDate = isAdmin(me?.role) || !!me?.can_override_delivery_date || canPerm('pallets', 'override')
  // Empty = use server NOW() (today's tip). A YYYY-MM-DD string back-dates the
  // tipped_at so the tip lands on the calendar day it physically happened.
  const [tippedAtOverride, setTippedAtOverride] = useState('')
  // The field opens from "Back-date this tip…" and stays open while a past date is set.
  const [backDateOpen, setBackDateOpen] = useState(false)
  const backDated = !!tippedAtOverride && tippedAtOverride !== sastTodayYMD()

  const undoMutation = useMutation({
    mutationFn: async (tippingId: string) => {
      // Writes go through the isolated save client — the shared client's auth
      // lock can hold an RPC open forever on a busy page (repo standard).
      const db = saveDb ?? await ensureSaveDb()
      if (!db) throw new ValidationError('Session not ready — refresh and try again.')
      const { error } = await withTimeout(db.rpc('undo_tipping', { p_tip_id: tippingId }), 'Tipping: undo_tipping')
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tipping-history', delivery.delivery_id, activeRun.run_id] })
      qc.invalidateQueries({ queryKey: ['delivery-live', delivery.delivery_id] })
      qc.invalidateQueries({ queryKey: ['ready-to-tip', activeRun.run_id] })
      qc.invalidateQueries({ queryKey: ['deliveries'] })
      qc.invalidateQueries({ queryKey: ['runs'] })
      // An undone pallet tip frees the pallet: the board's cache re-reads it.
      qc.invalidateQueries({ queryKey: ['ready-to-tip-pallets'] })
    },
    onError: (e: Error) => { void alertDialog({ title: "Couldn't undo", message: tippingRefusal(e) ?? e.message, tone: 'danger' }) },
  })

  const tipMutation = useMutation({
    // One validation branch per tip rule.
    // fallow-ignore-next-line complexity
    mutationFn: async () => {
      // Local-only session read — getUser() does a network round-trip and
      // would hang the mutation offline before insertOrQueueTipping can run.
      const { data: { session } } = await supabase.auth.getSession()
      const user = session?.user
      if (!user) throw new ValidationError('Not authenticated')
      const check = validateTip({ gross, binQty, binsRemaining })
      if (!check.ok) throw new ValidationError(check.message)

      // Back-date — when the picker is set to anything other than today, stamp
      // tipped_at at noon SAST on the chosen day so the tip lands on that
      // calendar date unambiguously. Empty / today means "use server NOW()".
      const tippedAtIso = (canBackDate && tippedAtOverride && tippedAtOverride !== sastTodayYMD())
        ? `${tippedAtOverride}T12:00:00+02:00`
        : null

      // insertOrQueueTipping captures the row offline if Wi-Fi is down — the
      // packhouse can keep tipping without losing data. The background
      // flusher drains the queue when connectivity returns.
      return await insertOrQueueTipping({
        // Client-minted PK so a commit whose response is lost (flaky Wi-Fi)
        // replays on the SAME id → the retry collides (23505) instead of
        // inserting a second tip and double-counting bins+kg into the run.
        // Mirrors the pallet/delivery offline idempotency in offlineQueue.ts.
        tipping_id:      newUuid(),
        delivery_id:     delivery.delivery_id,
        run_id:          activeRun.run_id,
        bin_quantity:    binQty,
        gross_weight_kg: gross,
        tipped_by:       user.id,
        // The scanned pallet (spec #4006): the server checks it is this
        // delivery's, tipped whole and only once.
        ...(pallet ? { delivery_pallet_id: pallet.pallet_id } : {}),
        // Back-date only when set; otherwise tipping_log.tipped_at DEFAULT NOW()
        // takes over (the column is always insertable).
        ...(tippedAtIso ? { tipped_at: tippedAtIso } : {}),
      })
    },
    onSuccess: (status) => {
      qc.invalidateQueries({ queryKey: ['tipping-history', delivery.delivery_id, activeRun.run_id] })
      qc.invalidateQueries({ queryKey: ['delivery-live', delivery.delivery_id] })
      qc.invalidateQueries({ queryKey: ['ready-to-tip', activeRun.run_id] })
      qc.invalidateQueries({ queryKey: ['deliveries'] })
      qc.invalidateQueries({ queryKey: ['runs'] })
      // The board's pallet cache: mark this pallet tipped now (offline too),
      // so a second scan of its sticker is refused on the board, not by the
      // server after the operator has filled the form.
      if (pallet) {
        qc.setQueriesData<PalletWithTip[]>({ queryKey: ['ready-to-tip-pallets'] },
          rows => withPalletTipped(rows, pallet.pallet_id, new Date().toISOString()))
        qc.invalidateQueries({ queryKey: ['ready-to-tip-pallets'] })
      }
      setGrossKg('')
      ownTippedBins.current += binQty
      setAnotherStationNote(null)
      // A pallet tip does not change what this device tips by count.
      if (!pallet) setLastBinQty(binQty)
      // A back-date belongs to the paper tip it was set for (U12 S2): the next
      // tip is a live one, so the field closes and the date goes.
      setTippedAtOverride('')
      setBackDateOpen(false)
      setError(null)
      setSavedNote(status === 'queued'
        ? 'Saved on this device, sends when Wi-Fi is back.'
        : null)
      // A pallet is tipped whole: back to the board for the next scan.
      if (pallet) onDone()
    },
    // A refusal TIPPING_DOMAIN knows (the run closed after the board loaded —
    // mig 20261028100000, 23514) reads as the overlay's sentence; so does a
    // permission refusal (BF-04); any other failure keeps its own text.
    onError: (e: Error) => setError(tippingRefusal(e) ?? tipPermissionRefusal(e) ?? e.message)
  })

  const pct = tippedPercent(binsReceived, binsRemaining)
  const { delivery_note_number: dnNumber, client_ref_no: clientRef } =
    delivery as Delivery & { delivery_note_number?: string | null; client_ref_no?: string | null }

  return (
    <div className="min-h-full bg-canvas flex flex-col">
      {/* One truncating line, most useful first: the DN the operator matches
          against the paper note, then variety and block, then the
          grower's own reference. As two `badge` chips they took the whole
          phone line and truncated "Nadorcott · Block 14C · R-…" to "Na…". */}
      <PageHeader onBack={onDone} title={delivery.grower}
        subtitle={<>
          {dnNumber && <><span className="font-mono text-fg" title="Delivery note number">DN {dnNumber}</span> · </>}
          {delivery.variety}{delivery.block ? ` · Block ${delivery.block}` : ''}
          {clientRef && <> · <span className="font-mono text-fg-secondary" title="Grower's own reference">{clientRef}</span></>}
        </>} />

      <div className="flex-1 overflow-y-auto pb-8">
        {/* UF-04: the run is its own tappable chip, so a phone never cuts it off. */}
        <div className="px-4 pt-3">
          <Link to={`/runs/${activeRun.run_id}?tab=tipping`} state={returnHere} title="Open the run"
            className="inline-flex items-center gap-1 min-h-[44px] max-w-full rounded-full border border-border px-3 text-sm text-fg">
            <span className="truncate">Tipping into {activeRun.run_number}</span>
            <ChevronRight size={14} aria-hidden="true" className="shrink-0" />
          </Link>
        </div>
        {/* UF-07: a finished delivery offers the next one on this run. A tap
            opens it; nothing switches by itself. A failed or pending read
            never says "none left". */}
        {binsRemaining === 0 && (
          <div className="px-4 pt-4" data-testid="next-delivery-offer">
            <div className="bg-surface rounded-xl p-4 space-y-3">
              <p className="font-bold text-success">
                {dnNumber ? `DN ${dnNumber}` : 'This delivery'} fully tipped · {binsReceived} of {binsReceived} bins
              </p>
              {(!waiting || waiting.status === 'loading') && (
                <p className="text-caption text-fg-muted">Looking for the next delivery on this run…</p>
              )}
              {waiting?.status === 'error' && (
                <p className="text-caption text-danger">Couldn't read the deliveries waiting on this run. Go back to the board to check.</p>
              )}
              {waiting?.status === 'ready' && waiting.items.length > 0 && (
                <>
                  <p className="text-caption text-fg-muted">Still waiting on this run</p>
                  {waiting.items.map(w => {
                    const wDn = (w as Delivery & { delivery_note_number?: string | null }).delivery_note_number
                    return (
                      <button key={w.delivery_id} type="button" onClick={() => onOpenNext?.(w)}
                        className="w-full min-h-[44px] flex items-center justify-between gap-3 rounded-lg bg-raised px-3 py-2 text-left active:scale-[0.98]">
                        <span className="min-w-0 truncate text-sm text-fg">
                          {wDn ? <span className="font-mono">DN {wDn}</span> : w.grower}
                          <span className="text-fg-muted">{wDn ? ` · ${w.grower}` : ''} · {w.variety}{w.block ? ` · Block ${w.block}` : ''} · {w.bins_remaining} bins</span>
                        </span>
                        <span className="text-cyan-400 text-caption font-semibold shrink-0">Tip →</span>
                      </button>
                    )
                  })}
                </>
              )}
              {waiting?.status === 'ready' && waiting.items.length === 0 && (
                <p className="text-caption text-fg-muted">No other delivery is waiting on this run.</p>
              )}
              <button type="button" onClick={onDone} className="min-h-[44px] px-3 text-sm text-fg-muted">← Back to the board</button>
            </div>
          </div>
        )}

        {/* Delivery progress */}
        <div className="px-4 pt-4 pb-2">
          <div className="bg-surface rounded-xl p-4 space-y-3">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-caption text-fg-muted">Bins remaining</p>
                <p className="text-3xl font-bold text-fg tabular-nums">{binsRemaining}</p>
              </div>
              <div className="text-right">
                <p className="text-caption text-fg-muted">Received</p>
                <p className="text-2xl font-bold text-fg tabular-nums">{binsReceived}</p>
              </div>
            </div>
            {/* Progress bar */}
            <div>
              <div className="flex justify-between text-caption text-fg-muted tabular-nums mb-1">
                <span>{pct}% tipped this delivery</span>
                <span>{binsReceived - binsRemaining} of {binsReceived} bins</span>
              </div>
              <div className="h-3 bg-raised rounded-full overflow-hidden">
                <div
                  className="h-full bg-fg-secondary/70 rounded-full transition-[width]"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>

            {/* This delivery's totals on this run (D3: the history is filtered to one delivery) */}
            {history.length > 0 && (
              <div className="grid grid-cols-2 gap-2 pt-1 border-t border-line">
                <div>
                  <p className="text-caption text-fg-muted">Bins tipped from this delivery</p>
                  <p className="font-bold text-fg tabular-nums">{totalBinsTipped}</p>
                </div>
                <div>
                  <p className="text-caption text-fg-muted">Net weight from this delivery</p>
                  <p className="font-bold text-fg tabular-nums">{totalNetWeightKg.toFixed(1)} kg</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tipping entry form */}
        <div className="px-4 py-2 space-y-4">
          <div className="bg-surface rounded-xl p-4 space-y-4">
            <p className="font-semibold text-fg flex items-center gap-2">
              <Scale size={16} className="text-success" />
              Record Tipping
            </p>
            {pallet && (
              <p className="rounded-lg bg-raised px-3 py-2 text-sm text-fg" data-testid="tip-pallet">
                <span className="font-semibold">{palletOfText(pallet.pallet_no, pallet.count)}</span> · {pallet.crates} crates, tipped whole
              </p>
            )}

            {/* Gross weight */}
            <div>
              <label className="label">Gross Weight (kg) <span className="text-danger">*</span></label>
              <input
                type="text"
                inputMode="decimal"
                className="field text-2xl font-bold"
                placeholder="0.00"
                value={grossKg}
                onChange={e => setGrossKg(safeDecimal(e.target.value))}
                autoFocus
              />
              {/* The net on the tare the SERVER will apply to this delivery
                  (its bin type, then the grower's own tare, then Config) —
                  the net the operator checks is the net that is saved. */}
              <TipNetPreview deliveryId={delivery.delivery_id} gross={gross} binQty={binQty} />
              <TipRangeHint deliveryId={delivery.delivery_id} binTypeId={delivery.fruit_bin_type_id} containerType={delivery.fruit_container_type} gross={gross} binQty={binQty} />
              {intakeGross != null && grossKg !== String(intakeGross) && (
                <button type="button" onClick={() => setGrossKg(String(intakeGross))}
                  className="mt-2 min-h-[44px] text-sm font-semibold text-link underline">
                  Use intake weight ({intakeGross} kg)
                </button>
              )}
            </div>

            {/* Bin quantity stepper. Everyone gets a text input in the
                centre so they can type a number directly (R1, 2026-07-06:
                a 40-bin delivery is impractical to +/- through), plus the
                +/- buttons. The Save button keeps the
                binQty <= binsRemaining check regardless of role. */}
            <div>
              <label className="label flex items-center justify-between">
                <span>Bin Quantity</span>
                <span className="text-caption text-fg-muted normal-case tracking-normal">
                  type or use +/−
                </span>
              </label>
              <div className="flex items-center gap-4 bg-raised rounded-xl p-2">
                <button disabled={!!pallet}
                  className="w-14 h-14 rounded-xl bg-raised-hover hover:bg-gray-500 active:scale-95 text-2xl font-bold text-fg transition-[transform,background-color,opacity] flex items-center justify-center disabled:opacity-40"
                  onClick={() => { setAnotherStationNote(null); setBinQty(q => Math.max(1, q - 1)) }}>
                  −
                </button>
                {/* R1 (2026-07-06): typeable by everyone — a 40-bin delivery is
                    impractical to +/− through. Non-numeric/empty keystrokes clamp
                    to 1 so the value stays a valid integer; the Save button still
                    gates on binQty <= binsRemaining (canSubmitTip), so the soft
                    cap — not a role — is the real guard. */}
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={binQty} readOnly={!!pallet}
                  onChange={e => {
                    const raw = e.target.value.replace(/[^0-9]/g, '')
                    const n   = raw === '' ? 1 : Math.max(1, parseInt(raw, 10))
                    setAnotherStationNote(null)
                    setBinQty(n)
                  }}
                  onFocus={e => e.currentTarget.select()}
                  className="flex-1 bg-transparent text-3xl font-bold text-center text-fg tabular-nums focus:outline-none focus:bg-surface rounded-lg"
                  aria-label="Bin quantity"
                />
                <button disabled={!!pallet}
                  className="w-14 h-14 rounded-xl bg-raised-hover hover:bg-gray-500 active:scale-95 text-2xl font-bold text-fg transition-[transform,background-color,opacity] flex items-center justify-center disabled:opacity-40"
                  onClick={() => { setAnotherStationNote(null); setBinQty(q => clampBinQty(q + 1, binsRemaining)) }}>
                  +
                </button>
              </div>
              {anotherStationNote !== null && (
                <p role="status" className="mt-1 text-sm text-warning">
                  Another station tipped. Bins left is now {anotherStationNote}.
                </p>
              )}
              {/* The back-date link shares this row. On a line of its own it still left ✓ Tip
                  5px under the bottom nav at 1024×768 once the net-weight line showed (PW-11). */}
              <div className="mt-1 flex items-center justify-between gap-3">
                {canBackDate && !backDateOpen && !backDated ? (
                  <button type="button" onClick={() => setBackDateOpen(true)}
                    className="tap-target-y text-sm text-fg-muted hover:text-fg-secondary underline underline-offset-2">
                    Back-date this tip…
                  </button>
                ) : <span />}
                <p className="text-caption text-fg-muted text-right tabular-nums">
                  Max: {binsRemaining} bins remaining
                </p>
              </div>
            </div>

            {/* Back-date control. Admins always; non-admin operators with the
                can_override_delivery_date trust flag too — one flag covers
                Deliveries + Pallets + Tipping. Opened from the link on the Max row:
                always open, it pushed ✓ Tip under the bottom nav on a 1024×768
                floor tablet (packhouse audit PM-PRD-03 / PW-11). */}
            {canBackDate && (backDateOpen || backDated) && (
              <BackDateField value={tippedAtOverride} onChange={setTippedAtOverride} />
            )}

            {error && (
              <div className="rounded-xl bg-danger-soft border border-danger-edge px-4 py-3 text-sm text-danger">
                {error}
              </div>
            )}
            {savedNote && !error && (
              <p role="status" className="text-sm text-fg-secondary">{savedNote}</p>
            )}

            {canTip && (
            <Button
              variant="primary"
              fullWidth
              className="text-lg py-4"
              onClick={() => { setSavedNote(null); tipMutation.mutate() }}
              disabled={tipMutation.isPending || !canSubmitTip({ gross, binQty, binsRemaining }, netKg)}>
              {tipMutation.isPending
                ? 'Saving…'
                : `✓ Tip ${binQty} bin${binQty !== 1 ? 's' : ''}`}
            </Button>
            )}
            {tipBlocked && (
              <p role="note" className="rounded-xl bg-warning-soft border border-warning-edge px-4 py-3 text-sm text-warning">
                {TIP_VIEW_ONLY}
              </p>
            )}

            <SubmitOverlay inline
              isPending={tipMutation.isPending}
              isSuccess={tipMutation.isSuccess}
              isError  ={tipMutation.isError}
              error    ={tipMutation.error}
              domain={TIPPING_DOMAIN}
              successLabel="Tipped"
              onDismiss={() => tipMutation.reset()}
            />
          </div>

          {/* Tipping history for this delivery on this run */}
          {history.length > 0 && (
            <div className="bg-surface rounded-xl p-4 space-y-2">
              <p className="text-caption font-semibold text-fg-muted uppercase tracking-wider">
                Tips from this delivery on this run
              </p>
              {history.map((t, i) => (
                <div key={t.tipping_id}
                  className="flex items-center justify-between py-2 border-b border-line last:border-0">
                  <div className="flex items-center gap-2">
                    <span className="text-fg-muted text-caption w-4 tabular-nums">{history.length - i}.</span>
                    <span className="text-fg font-semibold tabular-nums">{t.bin_quantity} bins</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-sm text-fg tabular-nums">{fmtQty(t.net_weight_kg, 1, 1)} kg net</p>
                      <p className="text-caption text-fg-muted tabular-nums">
                        {new Date(t.tipped_at).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    {canUndo && (
                      <button
                        onClick={async () => {
                          if ((await confirmUndoTip({ tippingId: t.tipping_id, message: `Undo this tipping (${t.bin_quantity} bins · ${fmtQty(t.net_weight_kg, 1, 1)} kg)? Run + delivery aggregates will be recomputed.` }))) {
                            undoMutation.mutate(t.tipping_id)
                          }
                        }}
                        disabled={undoMutation.isPending}
                        className="min-h-[44px] px-3 inline-flex items-center gap-1.5 text-sm rounded-lg text-fg-muted hover:text-warning hover:bg-warning-soft disabled:opacity-50"
                        aria-label={`Undo tip of ${t.bin_quantity} bins`}
                        title="Undo this tipping">
                        <Undo2 size={14} /> Undo
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/** A waiting count that is only a number once its read has succeeded. */
function countOrNull(q: { isSuccess: boolean; data?: unknown[] }): number | null {
  return q.isSuccess && q.data ? q.data.length : null
}

/** UF-03 — the run strip under the board header. `deliveriesWaiting`
 *  undefined = this flow has no delivery list (berry), so the part is left out. */
function RunStrip({ kg, deliveriesWaiting, binsWaiting }: {
  kg: number | null | undefined
  deliveriesWaiting: number | null | undefined
  binsWaiting: number | null
}) {
  const n = (v: number | null) => (v === null ? '—' : String(v))
  return (
    <section aria-label="This run so far"
      className="bg-surface rounded-xl border border-line-subtle px-4 py-3 flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
      <p className="text-fg">
        <span className="text-title font-semibold">{typeof kg === 'number' ? fmtKg(kg) : '—'}</span>{' '}
        <span className="text-caption text-fg-muted">tipped on this run</span>
      </p>
      <p className="text-callout text-fg-secondary">
        {deliveriesWaiting !== undefined && <>{n(deliveriesWaiting)} {deliveriesWaiting === 1 ? 'delivery' : 'deliveries'} waiting · </>}
        {n(binsWaiting)} stored {binsWaiting === 1 ? 'bin' : 'bins'}
      </p>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Main Tipping Page
// ---------------------------------------------------------------------------
type WaitingByRun = Record<string, { deliveries: number; bins: number }>
const firstOf = (x: any) => (Array.isArray(x) ? x[0] : x)

function tally(byRun: WaitingByRun, rows: any[], kind: 'deliveries' | 'bins', waits: (x: any) => boolean) {
  for (const r of rows) {
    const x = firstOf(r[kind === 'deliveries' ? 'deliveries' : 'bins'])
    if (x && waits(x) && byRun[r.run_id]) byRun[r.run_id][kind]++
  }
}

/** What waits to tip on each listed run: deliveries with bins left, bins in the cold room. Two reads. */
async function readTippingWaiting(runIds: string[]): Promise<WaitingByRun> {
  const [dl, bn] = await Promise.all([
    supabase.from('run_deliveries').select('run_id, deliveries!inner(bins_remaining)').in('run_id', runIds).gt('deliveries.bins_remaining', 0),
    supabase.from('run_bins').select('run_id, bins!inner(status)').in('run_id', runIds).in('bins.status', ['IN_COLDROOM', 'REBINNED']),
  ])
  if (dl.error) throw dl.error
  if (bn.error) throw bn.error
  const byRun: WaitingByRun = {}
  for (const id of runIds) byRun[id] = { deliveries: 0, bins: 0 }
  tally(byRun, (dl.data ?? []) as any[], 'deliveries', d => d.bins_remaining > 0)
  tally(byRun, (bn.data ?? []) as any[], 'bins', b => b.status === 'IN_COLDROOM' || b.status === 'REBINNED')
  return byRun
}

// Pre-existing large page (run picker + three tip sections).
// fallow-ignore-next-line complexity
export default function TippingPage() {
  const returnHere = useReturnHereState()
  const navigate  = useNavigate()
  const canStartRun = useCanStartRun()
  const canOpenRuns = useCanOpenModule('runs')
  const qc        = useQueryClient()
  const saveDb    = useSaveDb()
  const flow      = useProductionFlow()
  const manifest  = useFlowManifest(flow)
  // Persisted so navigating into a delivery and back keeps you on the run.
  // F02 UF-16: the pick is remembered per business and for TODAY only (SAST).
  // A pick from yesterday reads as no pick, so the operator chooses the run on the line now.
  const [rawPickedRunId, setRawPickedRunId] = useTenantPersistedState<string | null>('kf49.tipping.pickedRunId', null)
  const [pickedDay, setPickedDay] = useTenantPersistedState<string | null>('kf49.tipping.pickedDay', null)
  const pickedRunId = pickedDay === sastTodayYMD() ? rawPickedRunId : null
  const setPickedRunId = (id: string | null) => { setRawPickedRunId(id); setPickedDay(id ? sastTodayYMD() : null) }
  // F02 UF-01: /tipping/run/:runId opens the station on that run. The link sets
  // the same tenant + today pick a tap on the list would. A run that is not
  // ACTIVE is dropped by the stale-pick effect below, so the list shows.
  // A malformed id never reaches the stored pick (uuidParam).
  const { runId: rawRouteRunId } = useParams<{ runId: string }>()
  const routeRunId = rawRouteRunId ? uuidParam(rawRouteRunId) ?? undefined : undefined
  const mountedAt = useRef(Date.now())
  useEffect(() => {
    if (routeRunId) setRawPickedRunId(routeRunId)
    if (routeRunId) setPickedDay(sastTodayYMD())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeRunId])
  // Leaving the run (Back, or it closed) also leaves the run address, so a refresh does not pick it again.
  const leaveRun = () => { setPickedRunId(null); if (routeRunId) navigate('/tipping', { replace: true }) }
  const [selected, setSelected] = useState<Delivery | null>(null)
  // The pallet sticker that opened `selected` (spec #4006); cleared with it.
  const [selectedPallet, setSelectedPallet] = useState<ScannedPallet | null>(null)
  useEffect(() => { if (!selected) setSelectedPallet(null) }, [selected])
  // Scan/type a bin number to find it in the stored-bins re-tip list — the
  // bin label's own Code 128 barcode (BinLabel.tsx) encodes the bin_number
  // directly, so a wedge scanner "typing" it here just filters the list.
  const [binScan, setBinScan] = useState('')

  // Back-date for the one-tap tips at this level (blueberry RELEASED batches +
  // stored-bin re-tips). Same trust as the in-form delivery back-date: admin or
  // the can_override_delivery_date flag. Empty / today → server NOW().
  const { data: me } = useCurrentUser()
  // UF-10: a manager signed in at the station may stop tipping from here. A
  // scale operator never sees it (the server refuses the write for them).
  const lifecycle = useRunLifecycle()
  const { can: canPerm2, data: permsMap2, isCustomRole: isCustomRole2 } = usePermissions()
  const canBackDate = isAdmin(me?.role) || !!me?.can_override_delivery_date || canPerm2('pallets', 'override')
  // BF-04: dolly and stored-bin tips need tipping:edit, like the delivery tip.
  const canTip = canPerm2('tipping', 'edit')
  const tipBlocked = !!permsMap2 && !canTip
  const [tipDateOverride, setTipDateOverride] = useState('')
  // A plain local reset: the save-feedback guard reads any set* call in a mutation body as a write.
  const clearTipDate = () => setTipDateOverride('')
  const tipBackDateIso = (canBackDate && tipDateOverride && tipDateOverride !== sastTodayYMD())
    ? `${tipDateOverride}T12:00:00+02:00`
    : null

  // Tipping is only allowed on runs that are still ACTIVE. We deliberately
  // don't auto-select a run here (no localStorage, no fall-back-to-most-recent)
  // — the operator must tap a run before they can tip, otherwise it's too easy
  // to dump bins onto the wrong run when several are open at once.
  const { data: tippingRuns = [], isLoading: runLoading, isFetching: runFetching, dataUpdatedAt: runsUpdatedAt, isError: runError, refetch: refetchRuns } = useQuery({
    queryKey: ['runs', 'selection', 'tipping'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('runs').select('*')
        .eq('status', 'ACTIVE')
        .is('deleted_at', null)
        .order('started_at', { ascending: false })
      if (error) throw error
      return data as Run[]
    },
    refetchInterval: POLL.live,
    refetchOnWindowFocus: true,
  })

  const activeRun = pickedRunId
    ? tippingRuns.find(r => r.run_id === pickedRunId) ?? null
    : null

  // F02 UF-16: when the station is dropped to the list, say why. Reads the
  // dropped run's stop stamp; the name is dropped (not the line) if that read fails.
  const [stopNote, setStopNote] = useState<string | null>(null)
  const latestStopRef = useRef<string | null>(null)
  const explainStop = async (runId: string) => {
    latestStopRef.current = runId
    try {
      const { data, error } = await supabase.from('runs')
        .select('run_number, tipping_stopped_at, tipping_stopped_by').eq('run_id', runId).maybeSingle()
      if (error || !data?.tipping_stopped_at) return
      const hm = sastHM(data.tipping_stopped_at)
      const names = await fetchUserNames([data.tipping_stopped_by])
      const who = data.tipping_stopped_by ? names.get(data.tipping_stopped_by) : undefined
      if (latestStopRef.current !== runId) return
      setStopNote(`${data.run_number} stopped tipping${hm ? ` at ${hm}` : ''}${who ? ` (${who})` : ''}`)
    } catch { /* the list is shown either way */ }
  }

  // One read of what waits, for every ACTIVE run on the list (two reads, not N).
  const runIds = tippingRuns.map(r => r.run_id)
  const waitingQ = useQuery({
    queryKey: ['runs', 'tipping-waiting', runIds.join(',')],
    enabled: !activeRun && runIds.length > 0 && !manifest.usesForceCooling,
    refetchInterval: POLL.live,
    queryFn: () => readTippingWaiting(runIds),
  })
  // Berry fruit waits as released dollies, business-wide: a per-run count would read a confident 0.
  const waiting: RunWaitingRead | undefined = runIds.length === 0 || manifest.usesForceCooling ? undefined : {
    state: waitingQ.data ? 'ok' : waitingQ.isError ? 'error' : 'loading',
    byRun: waitingQ.data ?? {},
  }

  // If the picked run becomes ineligible (e.g. another device moved it to
  // TIPPING_COMPLETE) drop back to the runs list rather than letting the
  // operator keep tipping into a stale selection.
  useEffect(() => {
    if (pickedRunId && !runLoading) {
      // A run handed by the address is judged on a list fetched AFTER this page
      // opened: a cached list predates a run that was just created. And while
      // the address names a run, an older stored pick is not this run's verdict.
      if (routeRunId && (pickedRunId !== routeRunId || runFetching || runsUpdatedAt < mountedAt.current)) return
      const stillExists = tippingRuns.some(r => r.run_id === pickedRunId)
      if (!stillExists) {
        const droppedId = pickedRunId
        leaveRun()
        setSelected(null)
        void explainStop(droppedId)
      }
    }
  }, [pickedRunId, runLoading, runFetching, runsUpdatedAt, tippingRuns])

  // Deliveries linked to the active run with bins remaining. CITRUS only —
  // blueberry fruit is never tipped straight off a delivery (it must pass the
  // force-cool gate and arrive here as a RELEASED batch; mig 822 enforces this
  // at the DB even if the UI is bypassed).
  const readyToTipQ = useQuery({
    queryKey: ['ready-to-tip', activeRun?.run_id],
    enabled: !!activeRun && !manifest.usesForceCooling,
    refetchInterval: POLL.live,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('run_deliveries')
        .select('delivery_id, deliveries(*)')
        .eq('run_id', activeRun!.run_id)
      if (error) throw error
      return (data ?? [])
        .map((r: { delivery_id: string; deliveries: Delivery | Delivery[] }) =>
          Array.isArray(r.deliveries) ? r.deliveries[0] : r.deliveries)
        .filter((d): d is Delivery => !!d && d.bins_remaining > 0)
        .sort((a, b) => new Date(a.received_at).getTime() - new Date(b.received_at).getTime())
    }
  })
  const readyToTip = readyToTipQ.data ?? []
  // Pallet stickers (spec #4006): the board's deliveries' pallets, cached so a
  // scan resolves offline. Only where the business weighs pallets at intake.
  const weighsAtIntake = capabilityOn(useActiveTenant().data?.capabilities?.intake_weight)
  const readyIds = readyToTip.map(d => d.delivery_id)
  const boardPalletsQ = useQuery({
    queryKey: ['ready-to-tip-pallets', activeRun?.run_id, readyIds.join(',')],
    enabled: !!activeRun && weighsAtIntake && readyIds.length > 0,
    refetchInterval: POLL.live,
    queryFn: () => fetchPalletsFor(readyIds),
  })

  // Stored bins linked to the active run via run_bins. These are re-tips —
  // each bin consumes in one shot (status -> TIPPED).
  const readyBinsQ = useQuery({
    queryKey: ['ready-bins', activeRun?.run_id],
    enabled: !!activeRun,
    refetchInterval: POLL.live,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('run_bins')
        .select('bin_id, bins(*, coldrooms:coldroom_id(code))')
        .eq('run_id', activeRun!.run_id)
      if (error) throw error
      return (data ?? [])
        .map((r: any) => Array.isArray(r.bins) ? r.bins[0] : r.bins)
        // Placed bins and bins stripped off a received pallet that were never
        // placed (REBINNED) — retip_bin accepts both (mig 20261019110000).
        .filter((b: any) => b && (b.status === 'IN_COLDROOM' || b.status === 'REBINNED'))
        .sort((a: any, b: any) => new Date(a.born_date).getTime() - new Date(b.born_date).getTime())
    }
  })
  const readyBins = readyBinsQ.data ?? []
  const scanQuery = binScan.trim().toUpperCase()
  // The bin number, or the pallet a bulk-sort pallet was saved as first (its
  // old sticker, mig 20261313000000).
  const filteredReadyBins = scanQuery
    ? readyBins.filter((b: any) => (b.bin_number ?? '').toUpperCase().includes(scanQuery)
        || (b.source_pallet_id ?? '').toUpperCase().includes(scanQuery))
    : readyBins

  // Released cooling batches (blueberry flow) — a pool of force-cooled, signed-off
  // batches ready for production. Tipping one links it to the active run and flips
  // it to TIPPED via the fail-closed RPC. Only RELEASED batches are tippable; the
  // RPC + DB CHECK reject anything else regardless of what's shown. The grower
  // rides along (batch → delivery embed) so two growers' identical-variety
  // dollies are distinguishable before the one-tap tip (audit FC-06).
  const readyBatchesQ = useQuery({
    queryKey: ['ready-batches'],
    // Enabled on any active run, not on the manifest flag: a flag-merge
    // misconfiguration must degrade to an EMPTY batch list, never hide real
    // released batches (FC-39). Citrus tenants simply have zero rows.
    enabled: !!activeRun,
    refetchInterval: POLL.live,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cooling_batches')
        .select('batch_id, batch_number, crate_type, crate_count, net_weight_kg, variety, released_at, delivery:deliveries(grower, puc)')
        .eq('status', 'RELEASED')
        .order('released_at', { ascending: true })
      if (error) throw error
      // fallow-ignore-next-line complexity
      return (data ?? []).map((r: any) => {
        const d = Array.isArray(r.delivery) ? r.delivery[0] : r.delivery
        return {
          batch_id:      r.batch_id,
          batch_number:  r.batch_number,
          crate_type:    r.crate_type,
          crate_count:   r.crate_count,
          net_weight_kg: r.net_weight_kg,
          variety:       r.variety,
          released_at:   r.released_at,
          grower:        d?.grower ?? null,
          puc:           d?.puc ?? null,
        } as ReadyBatch
      })
    },
  })
  const readyBatches = readyBatchesQ.data ?? []
  // F02 UF-09: a DUO run draws from BOTH of its fruits. The varieties of those two
  // fruits count as this run's own, so the second fruit raises no red warning. While
  // this is unread the board behaves as before (only the run's own variety matches).
  const duoFormatId = (activeRun as { duo_format_id?: string | null } | null)?.duo_format_id ?? null
  const duoVarietiesQ = useQuery({
    queryKey: ['duo-run-varieties', duoFormatId],
    enabled: !!duoFormatId,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data: f, error: fe } = await supabase.from('duo_pack_formats')
        .select('commodity_a_id, commodity_b_id').eq('duo_format_id', duoFormatId!).maybeSingle()
      if (fe) throw fe
      if (!f) return [] as string[]
      const { data, error } = await supabase.from('commodity_varieties')
        .select('name').in('commodity_id', [f.commodity_a_id, f.commodity_b_id])
      if (error) throw error
      return (data ?? []).map((r: { name: string }) => r.name)
    },
  })
  const duoVarieties = useMemo(() => new Set(duoVarietiesQ.data ?? []), [duoVarietiesQ.data])
  const dollySplit = useMemo(
    () => activeRun ? splitDolliesForRun(readyBatches, activeRun, duoVarieties) : { mine: readyBatches, others: [] as ReadyBatch[] },
    [readyBatches, activeRun, duoVarieties])
  // W0 D1 — the Precool board's "Tip in Tipping" names the dolly (?batch=<id>):
  // its card is scrolled into view and marked. Nothing is tipped by the link;
  // a junk id, or one not in the released list, leaves the page plain.
  const [searchParams] = useSearchParams()
  const markedBatchId = tippingBatchParam(searchParams.get('batch'))
  const markedCardRef = useRef<HTMLButtonElement | null>(null)
  const markedPresent = !!markedBatchId && readyBatches.some(b => b.batch_id === markedBatchId)
  useEffect(() => {
    if (markedPresent) markedCardRef.current?.scrollIntoView?.({ block: 'center' })
  }, [markedPresent])
  // Combined render state for the board's shared empty claim. A DISABLED
  // query (flow gating above) reports isPending with isFetching false, so it
  // never traps the gate in loading — and never blocks the honest empty.
  const boardQs = [readyToTipQ, readyBinsQ, readyBatchesQ]
  const boardState = {
    isPending:  boardQs.some(q => q.isPending),
    isFetching: boardQs.some(q => q.isFetching),
    isError:    boardQs.some(q => q.isError),
    isPaused:   boardQs.some(q => q.isPaused),
    error:      boardQs.find(q => q.isError)?.error,
    refetch:    () => boardQs.forEach(q => void q.refetch()),
  }

  const tipBatchMutation = useMutation({
    // Tier 3: offline-tolerant via tipOrQueueBatch. p_tipped_at always carries
    // the moment the operator confirmed (or the manager's back-date) — online
    // that's "now" to within seconds; on an offline replay it's the honest
    // time instead of the drain time. The RPC's own bounds (not future, not
    // before released_at) remain the wall.
    // fallow-ignore-next-line complexity
    mutationFn: async (batchId: string) => {
      if (!activeRun) throw new ValidationError('No active run')
      return tipOrQueueBatch(batchId, {
        p_run_id:   activeRun.run_id,
        p_tipped_at: tipBackDateIso ?? new Date().toISOString(),
      })
    },
    onSuccess: (status) => {
      clearTipDate()  // U12 S2: the back-date was for this one tip; the next is live
      if (status !== 'queued') playTone({ freq: 1800, ms: 120, gain: 0.2, buzz: 'good' })  // UF-09: a tip is heard, tapped or scanned
      qc.invalidateQueries({ queryKey: ['ready-batches'] })
      qc.invalidateQueries({ queryKey: ['cooling-batches'] })
      qc.invalidateQueries({ queryKey: ['runs'] })
      if (status === 'queued') {
        void alertDialog({
          title: 'Tip saved offline',
          message: 'No connection — the tip is queued with the time you confirmed it, and lands on the run the moment Wi-Fi returns. The batch stays listed until then.',
        })
      }
    },
    onError: (e: Error) => { void alertDialog({ title: "Couldn't tip batch", message: tippingRefusal(e) ?? tipPermissionRefusal(e) ?? e.message, tone: 'danger' }) },
  })

  // One confirm for a released dolly, shared by the card tap and the label
  // scan (Tier 2, 2026-07-30) — same facts, same mismatch warnings, so a scan
  // can never skip the check a tap would have shown (FC-06). Kept as the
  // confirm-tap-after-scan per Jason's decision.
  // One branch per mismatch warning + confirm outcome.
  // fallow-ignore-next-line complexity
  const confirmAndTipBatch = async (b: ReadyBatch) => {
    if (!activeRun) return
    // A scanned label reaches here without a button to disable — say why.
    if (!canTip) {
      void alertDialog({ title: "Can't tip", message: TIP_NEEDS_PERMISSION, tone: 'danger' })
      return
    }
    const growerMismatch  = !!(b.grower && activeRun.grower && b.grower !== activeRun.grower)
    const varietyMismatch = !!(b.variety && activeRun.variety && b.variety !== activeRun.variety) && !dollyMatchesRun(b, activeRun, duoVarieties)
    const warn = [
      growerMismatch  ? `⚠ Grower differs: batch is ${b.grower}, run is ${activeRun.grower}.` : null,
      varietyMismatch ? `⚠ Variety differs: batch is ${b.variety}, run is ${activeRun.variety}.` : null,
    ].filter(Boolean).join('\n')
    const msg = `Tip ${b.batch_number}${b.grower ? ` (${b.grower})` : ''} — ${Math.round(b.net_weight_kg)} kg net into ${activeRun.run_number}?${warn ? `\n\n${warn}` : ''}`
    if (await confirmDialog({ message: msg, tone: warn ? 'danger' : 'warning' })) {
      tipBatchMutation.mutate(b.batch_id)
    }
  }

  // Dolly-label scans (Tier 2): the label's Code 128 is the batch number.
  // Scan → the SAME confirm as tapping the card; scanning with no run picked
  // gets a spoken-out refusal (deliberate — runs are never auto-selected).
  // One branch per refusal path.
  // fallow-ignore-next-line complexity
  const onBatchScan = (num: string, beep = true) => {
    if (!activeRun) {
      playTone({ freq: 220, ms: 250, gain: 0.35, type: 'square', buzz: 'bad' })
      void alertDialog({ title: 'Pick a run first', message: `Scanned ${num} — select the destination run, then scan the dolly label again.` })
      return
    }
    const b = readyBatches.find(x => x.batch_number === num)
    if (!b) {
      playTone({ freq: 220, ms: 250, gain: 0.35, type: 'square', buzz: 'bad' })
      void alertDialog({
        title: 'Not ready to tip',
        message: `${num} isn't a released batch. It may be unreleased (see the Force-Cooling board) or already tipped.`,
        tone: 'danger',
      })
      return
    }
    if (beep) playScanBeep()
    void confirmAndTipBatch(b)
  }
  useScanWedge(matchBatchNumber, onBatchScan)
  const [showCamScan, setShowCamScan] = useState(false)

  // Re-tip a stored bin: insert one tipping_log row + flip bin to TIPPED.
  // The bin row already carries gross/net weights — no operator entry needed.
  // Version-checked so a bin can only be tipped once even if two operators
  // hit the button simultaneously.
  // A part-tip leaves crates on a bulk-sort pallet: its sticker is now out of
  // date, so the board offers Reprint until the next tip (Thomas, 2 Nov).
  const [reprint, setReprint] = useState<{ binId: string; binNumber: string; left: number } | null>(null)
  const tipBinMutation = useMutation({
    // fallow-ignore-next-line complexity
    mutationFn: async (bin: { bin_id: string; bin_number: string; gross_weight_kg: number; version: number; crates?: number; crates_left?: number | null }) => {
      if (!activeRun) throw new ValidationError('No active run')
      const { data: { session } } = await supabase.auth.getSession(); const user = session?.user
      if (!user) throw new ValidationError('Not authenticated')

      // Atomic re-tip (mig 640): version-checked tipping_log insert (net taken
      // from the bin's own snapshotted net, not a generic resolver tare) + the
      // bin → TIPPED flip + the audit row, all in ONE transaction. The old
      // client flipped the bin BEFORE the insert, and the insert ALWAYS failed
      // the delivery_id NOT NULL constraint — so every re-tip stranded the bin.
      const db = saveDb ?? await ensureSaveDb()
      if (!db) throw new ValidationError('Session not ready — refresh and try again.')
      const { error } = await withTimeout(db.rpc('retip_bin', {
        p_bin_id:           bin.bin_id,
        p_run_id:           activeRun.run_id,
        p_expected_version: bin.version,
        // Back-date only when set; the RPC defaults p_tipped_at to NOW().
        ...(tipBackDateIso ? { p_tipped_at: tipBackDateIso } : {}),
        // A portion of a bulk-sort pallet (T6); none = the whole bin.
        ...(bin.crates ? { p_crates: bin.crates } : {}),
      }), 'Tipping: retip_bin')
      if (error) {
        if (isOptlk(error)) {
          throw new ValidationError('This bin was just tipped by someone else. Refresh and pick another.')
        }
        // BF-04: retip_bin's permission refusal (a device whose profile caps
        // tipping gets past the screen gate) as the sentence naming the right.
        const refusal = tipPermissionRefusal(error)
        if (refusal) throw new ValidationError(refusal)
        throw error
      }
    },
    onMutate: () => setReprint(null),
    onSuccess: (_d, bin) => {
      clearTipDate()  // U12 S2, as for a dolly
      const left = cratesLeftAfterTip(bin.crates_left, bin.crates)
      setReprint(left == null ? null : { binId: bin.bin_id, binNumber: bin.bin_number, left })
      qc.invalidateQueries({ queryKey: ['ready-bins', activeRun?.run_id] })
      qc.invalidateQueries({ queryKey: ['runs'] })
      qc.invalidateQueries({ queryKey: ['coldroom-occupied'] })
      qc.invalidateQueries({ queryKey: ['coldroom-bin-occupied'] })
      qc.invalidateQueries({ queryKey: ['coldroom-bins'] })
    },
    // A refusal (run stopped elsewhere, bin already tipped) or a timeout used
    // to vanish: no onError, no overlay (TIP-01). The inline overlay on the
    // stored-bins list now says why — the BF-04 permission refusal included
    // (the mutationFn rethrows it as its sentence), so it no longer needs an
    // alert of its own; re-read the list so a bin that left stock drops off it. ['runs'] is left to its poll on purpose — refetching it now
    // would swap a stopped run's board for the run list and take the sentence
    // with it.
    onError: () => {
      qc.invalidateQueries({ queryKey: ['ready-bins', activeRun?.run_id] })
    },
  })

  // One road for every stored-bin tip (scan, Enter, tap): how many crates
  // when a bulk-sort pallet can be split between lines (T6), then the confirm,
  // with the class / size warning first (T5). retip_bin re-checks all of it.
  const confirmBinTip = async (b: any): Promise<boolean> => {
    if (!activeRun) return false
    const crates = await askPortionCrates(b, activeRun.run_number)
    if (crates === null) return false
    const offFeed = feedWarning(b, activeRun as LinkedRunFields)
    const question = tipBinQuestion(b, activeRun.run_number, crates)
    if (!(await confirmDialog({ message: offFeed ? `${offFeed}\n\n${question}` : question, tone: 'warning' }))) return false
    tipBinMutation.mutate({
      bin_id: b.bin_id, bin_number: b.bin_number, gross_weight_kg: b.gross_weight_kg, version: b.version,
      ...(crates ? { crates, crates_left: b.crates_remaining } : {}),
    })
    return true
  }

  // F02 UF-05: a stored-bin label scanned anywhere on the board (or Enter on a
  // typed bin number) finds the bin by number, linked to this run or not. The
  // same one confirm as tapping a linked bin's card. A bin of another grower
  // or variety is REFUSED with a sentence (the rule lives here, as at New
  // Run; retip_bin itself checks neither). Every refusal is spoken out.
  const binScanBusy = useRef(false)
  const binTenantId = useActiveTenantId()
  // One branch per refusal path.
  // fallow-ignore-next-line complexity
  const onBinScan = async (num: string, beep = true) => {
    const refuse = (title: string, message: string) => {
      playTone({ freq: 220, ms: 250, gain: 0.35, type: 'square', buzz: 'bad' })
      void alertDialog({ title, message, tone: 'danger' })
    }
    if (!activeRun) {
      refuse('Pick a run first', `Scanned ${num} — select the destination run, then scan the bin label again.`)
      return
    }
    if (!canTip) { refuse("Can't tip", TIP_NEEDS_PERMISSION); return }
    if (binScanBusy.current || tipBinMutation.isPending) return
    binScanBusy.current = true
    try {
      let bin: any = readyBins.find((b: any) => (b.bin_number ?? '').toUpperCase() === num)
      if (!bin) {
        // Bin numbers are per business: the lookup names the business, it does
        // not lean on RLS alone (a platform admin sees every business's bins).
        if (!binTenantId) {
          refuse("Couldn't look up the bin", `${num}: check the connection and scan again.`)
          return
        }
        const { data, error } = await supabase
          .from('bins')
          .select('*, coldrooms:coldroom_id(code), src:source_run_id(commodity, run_kind)')
          .eq('tenant_id', binTenantId)
          .eq('bin_number', num)
          .is('deleted_at', null)
          .maybeSingle()
        if (error) {
          refuse("Couldn't look up the bin", `${num}: check the connection and scan again.`)
          return
        }
        if (!data) { refuse('No such bin', `${num} isn't a stored bin on this site.`); return }
        bin = Array.isArray(data) ? data[0] : data
        if (bin.status !== 'IN_COLDROOM' && bin.status !== 'REBINNED') {
          refuse('Not in stock', `${num} is ${String(bin.status).replace(/_/g, ' ').toLowerCase()} — only a stored bin can be tipped.`)
          return
        }
        // Linked runs (mig 20261288000000): grower, then variety, or commodity
        // on a run of every variety; never a bulk-sort pallet back onto a bulk
        // sort. retip_bin holds the same rules.
        const src = Array.isArray(bin.src) ? bin.src[0] : bin.src
        const refusal = binRefusal({ ...bin, source_commodity: src?.commodity, source_run_kind: src?.run_kind }, activeRun)
        if (refusal) { refuse(refusal.title, refusal.message); return }
      }
      if (beep) playScanBeep()
      if (await confirmBinTip(bin)) setBinScan('')
    } finally {
      binScanBusy.current = false
    }
  }
  // Off while a delivery's tip form is open (a label scanned there must not open
  // a stored-bin confirm over it) and on a berry board (no stored-bin tips there).
  useScanWedge(matchBinNumber, (num) => { void onBinScan(num) }, { enabled: !selected && !manifest.usesForceCooling })

  // Spec #3956 §4: a delivery sticker (DLV-…) scanned on the board. On this
  // run with crates left → the same tip form a tap opens. Not on the run → the
  // add-delivery path's own rule (this run's grower + variety, In Stock or
  // Received), then the form. Everything else is refused out loud. A delivery
  // off the board's list needs one read; offline that read fails and says so.
  const deliveryScanBusy = useRef(false)
  // One branch per refusal path.
  // fallow-ignore-next-line complexity
  const refuseScan = (title: string, message: string) => {
    playTone({ freq: 220, ms: 250, gain: 0.35, type: 'square', buzz: 'bad' })
    void alertDialog({ title, message, tone: 'danger' })
  }
  // A pallet sticker can open its delivery only while every crate of the
  // pallet is still there to tip, and only once.
  const palletOk = (pallet: ScannedPallet | undefined, d: Delivery): boolean => {
    const r = pallet ? palletScanRefusal(pallet, pallet.count, d.bins_remaining) : null
    if (r) refuseScan(r.title, r.message)
    return !r
  }
  // fallow-ignore-next-line complexity
  const onDeliveryScan = async (code: string, beep = true, pallet?: ScannedPallet) => {
    if (deliveryScanBusy.current) return
    deliveryScanBusy.current = true
    const refuse = refuseScan
    try {
      const deliveryId = deliveryIdFromLabel(code)
      const base = { code, run: activeRun ?? null, onRun: readyToTip, deliveryId }
      let decision = decideDeliveryScan<Delivery>(base)
      if (decision.kind === 'refuse') { refuse(decision.title, decision.message); return }
      if (decision.kind === 'open') {
        if (!canTip) { refuse("Can't tip", TIP_NEEDS_PERMISSION); return }
        if (!palletOk(pallet, decision.delivery)) return
        if (beep) playScanBeep()
        setSelectedPallet(pallet ?? null)
        setSelected(decision.delivery)
        return
      }
      let looked: Delivery | null | 'error' = 'error'
      if (binTenantId) {
        const { data, error } = await supabase.from('deliveries')
          .select('*')
          .eq('tenant_id', binTenantId)
          .eq('delivery_id', deliveryId)
          .is('deleted_at', null)
          .maybeSingle()
        looked = error ? 'error' : (data as Delivery | null)
      }
      decision = decideDeliveryScan<Delivery>({ ...base, looked })
      if (decision.kind === 'refuse') { refuse(decision.title, decision.message); return }
      if (decision.kind !== 'add' || !activeRun) return
      if (!canTip) { refuse("Can't tip", TIP_NEEDS_PERMISSION); return }
      if (!palletOk(pallet, decision.delivery)) return
      if (beep) playScanBeep()
      const d = decision.delivery
      const dn = d.delivery_note_number ? ` (DN ${d.delivery_note_number})` : ''
      if (!await confirmDialog({ message: `This delivery${dn} is not on ${activeRun.run_number} yet. Add it to the run and tip it?`, tone: 'warning' })) return
      try {
        await linkDeliveryToRun(supabase, activeRun.run_id, d.delivery_id)
      } catch (e) {
        refuse("Couldn't add the delivery", (e as Error).message)
        return
      }
      invalidateAfterLink(qc, activeRun.run_id, d.delivery_id)
      setSelectedPallet(pallet ?? null)
      setSelected(d)
    } finally {
      deliveryScanBusy.current = false
    }
  }
  // A pallet sticker (DLP-): find the pallet (the board's cache, else a read),
  // then the same path as its delivery's sticker, with the pallet attached.
  const onPalletScan = async (code: string, beep = true) => {
    const palletId = deliveryIdFromLabel(code)
    const cached = boardPalletsQ.data?.find(x => x.pallet_id === palletId)
    let p: ScannedPallet | null
    if (cached) {
      p = { ...cached, count: boardPalletsQ.data!.filter(x => x.delivery_id === cached.delivery_id).length }
    } else {
      try { p = await fetchPallet(palletId) } catch {
        refuseScan("Couldn't look up the pallet", `${code}: check the connection and scan again.`)
        return
      }
    }
    if (!p) { refuseScan('No such pallet', `${code} isn't a pallet on this site, or it was taken off its delivery.`); return }
    await onDeliveryScan(deliveryLabelCode(p.delivery_id), beep, p)
  }
  const onIntakeStickerScan = (code: string, beep = true) => {
    if (isDeliveryPalletLabel(code)) void onPalletScan(code, beep)
    else void onDeliveryScan(code, beep)
  }
  useScanWedge(matchIntakeSticker, (code) => { onIntakeStickerScan(code) }, { enabled: !selected && !manifest.usesForceCooling })

  // L3 — tipping form for one delivery
  if (selected && activeRun) {
    return (
      <TippingForm
        key={`${selected.delivery_id}:${selectedPallet?.pallet_id ?? ''}`}
        delivery={selected}
        activeRun={activeRun}
        pallet={selectedPallet}
        onDone={() => setSelected(null)}
        waiting={{
          // A refetch after the last tip is "loading": the finished delivery
          // is still in the stale list until it lands.
          status: readyToTipQ.isError ? 'error'
            : (readyToTipQ.isLoading || readyToTipQ.isFetching) ? 'loading' : 'ready',
          items: readyToTip.filter(d => d.delivery_id !== selected.delivery_id),
        }}
        onOpenNext={d => { setSelectedPallet(null); setSelected(d) }}
      />
    )
  }

  // L1 — runs list. Operator must tap a run before they can tip anything.
  if (!activeRun) {
    return (
      <PickRunFirst
        title="High-Speed Tipping"
        runs={tippingRuns}
        isLoading={runLoading}
        isError={runError}
        onRetry={() => void refetchRuns()}
        onBack={() => navigate('/')}
        onPick={id => { latestStopRef.current = null; setStopNote(null); setPickedRunId(id); if (routeRunId) navigate('/tipping', { replace: true }) }}
        emptyTitle="No runs are currently tipping"
        emptyHint={canStartRun && canOpenRuns === true
          ? 'Start a run to begin tipping.'
          : 'A manager, scale operator or admin starts the run.'}
        emptyAction={canStartRun && canOpenRuns === true ? (
          <Button variant="primary" onClick={() => navigate(newRunHref())}>Start a run</Button>
        ) : undefined}
        pickHint="Pick a run to tip into"
        hideStatus
        hidePackout
        houseHeader
        showStarted
        waiting={waiting}
        notice={stopNote}
      />
    )
  }

  // L2 — deliveries + stored bins for the picked run
  // One released dolly card (UF-09: shared by this run's list and the folded rest).
  // Wrong-batch tips were one anonymous confirm away (audit FC-06): surface grower
  // + a loud mismatch line vs the picked run before the tap lands. Soft check, the
  // server verifies tenancy/status.
  // fallow-ignore-next-line complexity
  const renderDolly = (b: ReadyBatch) => {
              const growerMismatch  = !!(b.grower && activeRun.grower && b.grower !== activeRun.grower)
              const varietyMismatch = !!(b.variety && activeRun.variety && b.variety !== activeRun.variety) && !dollyMatchesRun(b, activeRun, duoVarieties)
              return (
              <button
                key={b.batch_id}
                ref={b.batch_id === markedBatchId ? markedCardRef : undefined}
                data-marked={b.batch_id === markedBatchId ? 'true' : undefined}
                disabled={tipBatchMutation.isPending || !canTip}
                onClick={() => void confirmAndTipBatch(b)}
                className={`card w-full text-left active:scale-[0.98] transition-transform border-2 disabled:opacity-50 ${
                  b.batch_id === markedBatchId ? 'ring-2 ring-accent ' : ''}${
                  growerMismatch || varietyMismatch
                    ? 'border-warning-edge hover:border-warning/80'
                    : 'border-blue-700/40 hover:border-blue-500/60'}`}>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-900/40 border border-blue-700 rounded-lg flex items-center justify-center shrink-0">
                    <Package size={20} className="text-blue-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-mono font-bold text-blue-300 truncate">
                      {b.batch_number}
                      {b.released_at && <span className="ml-2 font-sans font-normal text-caption text-fg-muted">released {agoLabel(b.released_at)}</span>}
                    </p>
                    <p className="text-caption text-fg-muted truncate tabular-nums">
                      {b.grower && <span className={growerMismatch ? 'text-warning font-semibold' : 'text-fg-secondary'}>{b.grower} · </span>}
                      {b.crate_count} crates · {Math.round(b.net_weight_kg)} kg net
                      {b.variety ? <> · <span className={varietyMismatch ? 'text-warning font-semibold' : undefined}>{b.variety}</span></> : ''}
                    </p>
                    {(growerMismatch || varietyMismatch) && (
                      <p className="text-caption text-warning mt-0.5">
                        Doesn't match this run — check before tipping.
                      </p>
                    )}
                  </div>
                  <div className="text-blue-400 text-caption font-semibold shrink-0">Tip →</div>
                </div>
              </button>
            )
  }

  return (
    <div className="min-h-full bg-canvas flex flex-col">
      <MutationOverlays of={[[lifecycle.stopTipping, 'Tipping stopped']]} />
      <PageHeader onBack={() => { leaveRun(); setSelected(null) }} title={activeRun.run_number}
        subtitle={<>{activeRun.grower} · {isMixedRun(activeRun) ? `every ${(activeRun.commodity ?? '').toLowerCase()} variety` : activeRun.variety}</>}
        badge={<RunStatusBadge status={activeRun.status} size="xs" />}
        actions={<>
          <Link to={`/runs/${activeRun.run_id}?tab=tipping`} state={returnHere} className="btn-ghost btn-sm shrink-0 flex items-center gap-1"
            title="Open the run">
            <ExternalLink size={14} /> Open run
          </Link>
          {isManagerOrAbove(me?.role)
            && runActions(activeRun.status as RunStatus, { isAdmin: isAdmin(me?.role), isManager: true }).some(a => a.kind === 'stopTipping') && (
            <Button variant="ghost" size="sm" disabled={lifecycle.busy}
              className="shrink-0 text-amber-400 border-amber-800 hover:bg-amber-900/30"
              onClick={() => lifecycle.act('stopTipping', activeRun)}>
              Stop Tipping
            </Button>
          )}
          {/* Camera path for the dolly-label scan (Tier 2) — wedge guns need no
              button; this is the phone/tablet equivalent. */}
          {(readyBatches.length > 0 || !manifest.usesForceCooling) && (
            <Button variant="ghost" size="sm" aria-label="Scan" onClick={() => setShowCamScan(true)} title="Scan a dolly label or a delivery sticker with the camera"
              className="shrink-0 flex items-center gap-1">
              <ScanLine size={14} /> Scan
            </Button>
          )}
        </>} />

      {showCamScan && (
        <BadgeCameraScanModal heading={manifest.usesForceCooling ? 'Scan dolly label' : 'Scan delivery sticker'}
          makeDecoder={() => createFrameDecoder(tippingLabelFromTexts)}
          onScanned={(code) => {
            setShowCamScan(false)
            if (isDeliveryPalletLabel(code) || isDeliveryLabel(code)) onIntakeStickerScan(code, false)
            else onBatchScan(code, false)
          }}
          onClose={() => setShowCamScan(false)} />
      )}

      <div className="flex-1 p-4 space-y-3">

        {/* UF-03 — what this run has taken and what waits. Figures the page
            already holds (no new read). Dollies are left out on purpose:
            readyBatches is every Released batch in the business, not this
            run's. A count whose read is pending or failed shows a dash, never
            a confident 0. */}
        <RunStrip
          kg={activeRun.tipped_net_weight_kg}
          deliveriesWaiting={manifest.usesForceCooling ? undefined : countOrNull(readyToTipQ)}
          binsWaiting={countOrNull(readyBinsQ)} />

        {/* UF-02 — the last five tips of this run, any source, with Undo. */}
        <RecentTips runId={activeRun.run_id} canUndo={canUndoTip(me?.role, canPerm2, isCustomRole2) && (activeRun.status === 'ACTIVE' || activeRun.status === 'TIPPING_COMPLETE')} />

        {tipBlocked && (
          <p role="note" className="rounded-xl bg-warning-soft border border-warning-edge px-4 py-3 text-sm text-warning">
            {TIP_VIEW_ONLY}
          </p>
        )}

        {/* Shared back-date for the one-tap tips below (batches + stored bins).
            Delivery tips back-date inside their own form, so this only shows
            when there's a one-tap action to apply it to. */}
        {canBackDate && (readyBatches.length > 0 || readyBins.length > 0) && (
          <div className="bg-surface rounded-xl p-3">
            <label className="label">
              Tipped on <span className="text-fg-muted">(can back-date)</span>
            </label>
            <DateInput
              label="Tipped on"
              className="field"
              max={sastTodayYMD()}
              value={tipDateOverride}
              onChange={setTipDateOverride}
              hideHint={!!tipDateOverride && tipDateOverride !== sastTodayYMD()}
            />
            {tipBackDateIso && (
              <p className="text-caption text-warning mt-1">
                Back-dated — tips below will be stamped {tipDateOverride} 12:00 SAST.
              </p>
            )}
          </div>
        )}

        {/* Gate the board's empty claim — pending/paused/error must never
            render "No bins ready to tip" as truth (idle-wake audit UI-4). */}
        {readyToTip.length === 0 && readyBins.length === 0 && readyBatches.length === 0 && (
          <QueryStateGate query={boardState} noun={manifest.usesForceCooling ? 'batches' : 'bins'}
            empty={
              <TeachingEmptyState icon={Layers}
                status={manifest.usesForceCooling ? 'No batches ready to tip' : 'No bins ready to tip'}
                cue={manifest.usesForceCooling
                  ? 'Release a force-cooled batch in Force-Cooling — it will appear here ready to tip.'
                  : 'Tap "+ Truck not listed? Add a delivery to this run" below to put a truck on this run, or scan a stored bin.'} />
            } />
        )}

        {/* Released cooling batches (blueberry) — tip a force-cooled dolly into this run.
            UF-09: this run's dollies first; the rest fold under "Other growers (n)".
            The tick stays up while the result shows, so the last dolly leaving the
            list does not take it with it. */}
        {(readyBatches.length > 0 || !tipBatchMutation.isIdle) && (
          <div className="space-y-2">
            <p className="text-caption font-semibold text-fg-muted uppercase tracking-wider px-1 flex items-center gap-1.5">
              <Package size={12} /> Released batches · tip
            </p>
            <SubmitOverlay inline
              isPending={tipBatchMutation.isPending}
              isSuccess={tipBatchMutation.isSuccess && tipBatchMutation.data !== 'queued'}
              isError  ={tipBatchMutation.isError}
              error    ={tipBatchMutation.error}
              domain={TIPPING_DOMAIN}
              pendingLabel="Tipping…"
              successLabel="Tipped"
              onDismiss={() => tipBatchMutation.reset()} />
            {dollySplit.mine.map(renderDolly)}
            {dollySplit.others.length > 0 && (
              <details className="space-y-2" data-testid="other-dollies"
                open={dollySplit.others.some(b => b.batch_id === markedBatchId) || dollySplit.mine.length === 0}>
                <summary className="min-h-[44px] flex items-center px-1 text-sm text-fg-secondary cursor-pointer">
                  Other growers ({dollySplit.others.length})
                </summary>
                <div className="space-y-2">{dollySplit.others.map(renderDolly)}</div>
              </details>
            )}
          </div>
        )}

        {/* Stored bins — re-tip with one click each. Kept up while a tip is in
            flight or its result is showing, so the tick or the refusal
            survives the last bin leaving the list. */}
        {(readyBins.length > 0 || !tipBinMutation.isIdle || reprint || !manifest.usesForceCooling) && (
          <div className="space-y-2">
            <div>
              <p className="text-caption font-semibold text-fg-muted uppercase tracking-wider px-1 flex items-center gap-1.5">
                <Package size={12} /> Stored bins · re-tip
              </p>
              <SubmitOverlay inline
                isPending={tipBinMutation.isPending}
                isSuccess={tipBinMutation.isSuccess}
                isError  ={tipBinMutation.isError}
                error    ={tipBinMutation.error}
                domain={TIPPING_DOMAIN}
                pendingLabel="Tipping…"
                successLabel="Tipped"
                onDismiss={() => tipBinMutation.reset()} />
              {reprint && <BinReprintNotice {...reprint} onReprint={() => setReprint(null)} />}
              <p className="text-caption text-fg-muted px-1">
                Scan any stored {isMixedRun(activeRun) ? (activeRun.commodity ?? '') : activeRun.variety} bin of {activeRun.grower} to tip it, linked to this run or not.
              </p>
            </div>
            <input
              value={binScan}
              onChange={e => setBinScan(e.target.value)}
              /* fallow-ignore-next-line complexity */
              onKeyDown={async e => {
                if (e.key !== 'Enter' || tipBinMutation.isPending || !canTip) return
                if (filteredReadyBins.length !== 1) {
                  // Not in this run's linked list: a whole bin number is looked up by number.
                  const typed = matchBinNumber(binScan)
                  if (typed && filteredReadyBins.length === 0) void onBinScan(typed, false)
                  return
                }
                if (await confirmBinTip(filteredReadyBins[0])) setBinScan('')
              }}
              placeholder="Scan or type bin # to find it"
              className="field w-full font-mono" />
            {filteredReadyBins.map((b: any) => (
              <button
                key={b.bin_id}
                disabled={tipBinMutation.isPending || !canTip}
                onClick={() => void confirmBinTip(b)}
                className="card w-full text-left active:scale-[0.98] transition-transform border-2 border-line hover:border-line-strong disabled:opacity-50">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-raised border border-line rounded-lg flex items-center justify-center shrink-0">
                    <Package size={20} className="text-fg-secondary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-mono font-bold text-fg truncate flex items-center gap-2">
                      {b.bin_number}
                      <StatusPill object="bin" status={b.status} />
                    </p>
                    <p className="text-caption text-fg-muted tabular-nums">
                      {Math.round(b.net_weight_kg)} kg net
                      {b.coldrooms?.code && cellOrOverflow(b)
                        ? <> · {b.coldrooms.code}/{cellOrOverflow(b)}</>
                        : <> · not placed</>}
                    </p>
                  </div>
                  <div className="text-fg-secondary text-caption font-semibold shrink-0">Tip →</div>
                </div>
              </button>
            ))}
            {scanQuery && filteredReadyBins.length === 0 && (
              <p className="text-caption text-fg-muted px-1">No stored bin matches "{binScan}"</p>
            )}
          </div>
        )}

        {/* fallow-ignore-next-line complexity */}
        {readyToTip.map(d => {
          const pct = tippedPercent(d.bins_received, d.bins_remaining)
          // client_ref + delivery_note_number aren't on the shared Delivery
          // type yet; pulled through by the deliveries(*) join. mig 144
          // added client_ref_no; DN# is the packhouse-side note number.
          const dRow      = d as Delivery & { client_ref_no?: string | null; delivery_note_number?: string | null }
          const clientRef = dRow.client_ref_no       ?? null
          const dnNumber  = dRow.delivery_note_number ?? null

          return (
            <button
              key={d.delivery_id}
              className="card w-full text-left active:scale-[0.98] transition-transform space-y-3"
              onClick={() => setSelected(d)}>

              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-bold text-fg">{d.grower}</p>
                    {dnNumber && (
                      <span className="text-caption font-mono bg-raised/60 text-fg rounded-lg px-1.5 py-0.5"
                        title="Delivery note number">
                        DN {dnNumber}
                      </span>
                    )}
                    {clientRef && (
                      <span className="text-caption font-mono bg-raised/60 text-fg-secondary rounded-lg px-1.5 py-0.5"
                        title="Grower's own reference">
                        {clientRef}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-fg-muted">
                    {d.variety}{d.block ? ` · Block ${d.block}` : ''}
                    {d.puc ? ` · PUC ${d.puc}` : ''}
                  </p>
                </div>
                <ChevronRight size={20} className="text-fg-muted shrink-0 mt-1" />
              </div>

              <div className="flex items-center gap-4">
                <div>
                  <p className="text-caption text-fg-muted">Remaining</p>
                  <p className="text-2xl font-bold text-fg tabular-nums">{d.bins_remaining}</p>
                </div>
                <div>
                  <p className="text-caption text-fg-muted">Received</p>
                  <p className="text-xl font-semibold text-fg tabular-nums">{d.bins_received}</p>
                </div>
                <div className="flex-1">
                  <div className="h-2 bg-raised rounded-full overflow-hidden">
                    <div className="h-full bg-fg-secondary/70 rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                  <p className="text-caption text-fg-muted mt-0.5 text-right tabular-nums">{pct}% tipped</p>
                </div>
              </div>
            </button>
          )
        })}

        {!manifest.usesForceCooling && (
          <AddDeliveryToRunButton run={activeRun} label="+ Truck not listed? Add a delivery to this run"
            onAdded={(d) => setSelected(d)} />
        )}
      </div>
    </div>
  )
}

/**
 * The "Tipped on" back-date field. The form shows it only after "Back-date this
 * tip…" is tapped, or while a past date is set: an ordinary tip needs no date,
 * and always open it pushed ✓ Tip under the bottom nav on a 1024×768 floor
 * tablet for everyone holding back-date trust (packhouse audit PM-PRD-03 / PW-11).
 */
function BackDateField({ value, onChange }: { value: string; onChange: (ymd: string) => void }) {
  return (
    <div>
      <label className="label">
        Tipped on <span className="text-fg-muted">(can back-date)</span>
      </label>
      <DateInput
        label="Tipped on"
        className="field"
        max={sastTodayYMD()}
        value={value}
        onChange={onChange}
        hideHint={!!value && value !== sastTodayYMD()}
      />
      {value && value !== sastTodayYMD() && (
        <p className="text-caption text-warning mt-1">
          Back-dated — tipped_at will be {value} 12:00 SAST so the tip lands on that calendar day.
        </p>
      )}
    </div>
  )
}

/** T6: how many crates of a bulk-sort pallet go onto this line. undefined =
 *  not a pallet that splits (tip it whole); null = backed out or mistyped. */
async function askPortionCrates(b: { bin_number: string; crate_count?: number | null; crates_remaining?: number | null }, runNumber: string): Promise<number | undefined | null> {
  if (!canSplit(b)) return undefined
  const left = b.crates_remaining ?? 0
  const typed = await promptDialog({
    title: `How many crates go onto ${runNumber}?`,
    defaultValue: String(left), inputType: 'number', inputMode: 'numeric',
    hint: `${left} crates left on ${b.bin_number}. Leave it at ${left} to tip them all.`,
  })
  if (typed === null) return null
  const parsed = parsePortionCrates(typed, left)
  if ('error' in parsed) { void alertDialog({ title: 'Check the crates', message: parsed.error, tone: 'danger' }); return null }
  return parsed.crates
}
